import type { ConversationState } from "@ai-admin/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { trpc } from "../../app/trpc.js";
import { Badge, Chip, focusRing, Notice, PageTitle, Spinner, useWide } from "../../ui/index.js";
import { fmtAgo, useCabinet } from "../cabinet/index.js";
import { ChatPage } from "./ChatPage.js";
import { stateTone } from "./state.js";

type Filter = ConversationState | "all";
const FILTERS: Filter[] = ["escalated", "staff", "bot", "paused", "all"];

/**
 * Диалоги Instagram по состояниям (демо-чат здесь не показываем). Обновляется раз в 15 секунд.
 * На телефоне список, открытый диалог (`/conversations/:id`) занимает экран целиком.
 * На широком экране список слева, переписка справа; без выбранного диалога справа подсказка.
 */
export function ConversationsPage() {
  const { t } = useTranslation();
  const { conversationId } = useParams();
  const wide = useWide();
  // Переписка всегда на одной позиции в дереве: при пересечении 1024px (поворот планшета) она не перемонтируется,
  // черновик ответа и открытая шторка остаются. Высота на широком экране: экран минус отступы main и плашка админа.
  return (
    <div className="lg:grid lg:h-[calc(100dvh-4rem-var(--shell-banner,0px))] lg:grid-cols-[20rem_minmax(0,1fr)] lg:gap-6">
      {(wide || !conversationId) && <ConversationList selectedId={conversationId ?? null} />}
      {conversationId ? (
        <section className="flex min-h-0 flex-col">
          <ChatPage key={conversationId} />
        </section>
      ) : (
        wide && (
          <section className="flex min-h-0 flex-col">
            <div className="flex flex-1 items-center justify-center rounded-2xl bg-slate-50 text-sm text-slate-500">{t("conv.selectHint")}</div>
          </section>
        )
      )}
    </div>
  );
}

function ConversationList({ selectedId }: { selectedId: string | null }) {
  const { t, i18n } = useTranslation();
  const cab = useCabinet();
  const [filter, setFilter] = useState<Filter>("all");
  const list = useQuery(
    trpc.inbox.list.queryOptions(
      { salonId: cab.salonId, channel: "instagram", limit: 100, ...(filter !== "all" ? { state: filter } : {}) },
      { refetchInterval: 15_000 },
    ),
  );
  const now = new Date();
  return (
    <div className="flex min-h-0 flex-col">
      <PageTitle>{t("conv.title")}</PageTitle>
      <div className="-mx-4 mb-3 flex gap-2 overflow-x-auto px-4 lg:mx-0 lg:flex-wrap lg:px-0">
        {FILTERS.map((f) => (
          <Chip key={f} active={filter === f} onClick={() => setFilter(f)}>
            {t(`conv.filter.${f}`)}
          </Chip>
        ))}
      </div>
      {list.isPending && <Spinner />}
      {list.isError && <Notice kind="error">{t("common.error")}</Notice>}
      {list.data?.length === 0 && <Notice>{filter === "escalated" ? t("conv.noneWaiting") : t("conv.empty")}</Notice>}
      <div className="space-y-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-1">
        {list.data?.map((c) => (
          <Link
            key={c.id}
            to={`${cab.base}/conversations/${c.id}`}
            aria-current={c.id === selectedId ? "page" : undefined}
            className={`block rounded-2xl border bg-white px-4 py-3 hover:border-slate-400 ${focusRing} ${
              c.id === selectedId ? "border-slate-900 bg-slate-50" : c.state === "escalated" ? "border-amber-300" : "border-slate-200"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="truncate font-medium">{c.client_name ?? t("conv.instagramClient")}</div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs text-slate-400">{fmtAgo(new Date(c.updated_at), now, cab.timezone, i18n.language)}</span>
                <Badge tone={stateTone[c.state as ConversationState]}>{t(`conv.state.${c.state}`)}</Badge>
              </div>
            </div>
            {c.last_text && (
              <div className="mt-1 truncate text-sm text-slate-600">
                {c.last_author && c.last_author !== "client" && <span className="text-slate-400">{t(`conv.author.${c.last_author}`)}: </span>}
                {c.last_text}
              </div>
            )}
            {c.state === "escalated" && c.escalation_reason && (
              <div className="mt-1 text-xs text-amber-700">{t(`conv.reason.${c.escalation_reason}`, c.escalation_reason)}</div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
